import { Controller, FieldPath, FieldPathByValue, PathValue, useFormContext, useWatch } from "react-hook-form";
import { useEffect } from "react";
import { Fieldset, Textfield, ValidationMessage, Radio } from "@digdir/designsystemet-react";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';

import { useYearValidation } from "../pages/oppvarming/form/components/common/arstallInput/useYearValidation";
import { OppvarmingFormData } from "../pages/oppvarming/types";
import { RootState } from "../../../store/rootReducer";
import { VentilasjonFormData } from "../pages/ventilasjon/form/types";
import { useUndefinedIfTranslationMissing } from "../../../hooks/useUndefinedIfTranslationMissing";

type InstallasjonsArFormTypes = OppvarmingFormData | VentilasjonFormData;

interface InstallasjonsArRadioProps<T extends InstallasjonsArFormTypes = InstallasjonsArFormTypes> {
  name: FieldPath<T>;
  translationKeyPrefix?: string;
  installasjonsArKey?: string;
  vetInstallasjonsArKey?: string;
}

export const InstallasjonsArRadio = <T extends InstallasjonsArFormTypes = InstallasjonsArFormTypes>({ name, translationKeyPrefix, installasjonsArKey = 'installasjonsAr', vetInstallasjonsArKey = 'vetInstallasjonsAr' }: InstallasjonsArRadioProps<T>) => {
  const { t } = useTranslation();
  const installationIsKnownDescription = useUndefinedIfTranslationMissing(`${translationKeyPrefix}.input.description`);

  const { register, setValue, getFieldState, control, getValues } = useFormContext<T>();
  const { validateYearAndBuildYear, yearPattern } = useYearValidation();

  const installasjonsArFieldName = `${name}.${installasjonsArKey}` as FieldPathByValue<T, number | undefined>;
  const vetInstallasjonsArFieldName = `${name}.${vetInstallasjonsArKey}` as FieldPathByValue<T, boolean | undefined>;

  const installasjonsArFieldNameValue = useWatch({ control, name: installasjonsArFieldName }) as number | undefined;
  const knowsInstallationYearValue = useWatch({ control, name: vetInstallasjonsArFieldName }) as boolean | undefined;

  const byggAr = useSelector((state: RootState) => state.register.userInput?.bygningsdetaljer?.byggAr);

  useEffect(() => {
    if (knowsInstallationYearValue === false && byggAr !== undefined) {
      // Autofill with building year when the user doesn't know the installation year
      setValue(installasjonsArFieldName, byggAr as PathValue<T, FieldPathByValue<T, number | undefined>>, { shouldValidate: false });
    } else if (knowsInstallationYearValue === true && installasjonsArFieldNameValue === undefined) {
      // Only clear if currently empty, don't wipe a prefilled server value
      setValue(installasjonsArFieldName, undefined as PathValue<T, FieldPathByValue<T, number | undefined>>, { shouldValidate: false });
    }
  }, [knowsInstallationYearValue, byggAr, installasjonsArFieldName, installasjonsArFieldNameValue, setValue]);

  const { error: inputError } = getFieldState(installasjonsArFieldName);
  const { error: radioError } = getFieldState(vetInstallasjonsArFieldName);
  const errorMessage = radioError?.message || inputError?.message;
  const label = t(`${translationKeyPrefix}.label`);

  const vetInstallasjonsArDefaultValue = (getValues(vetInstallasjonsArFieldName) as boolean | undefined)
    ?? (installasjonsArFieldNameValue !== undefined ? true : undefined);

  return (
    <Controller
      control={control}
      name={vetInstallasjonsArFieldName}
      defaultValue={vetInstallasjonsArDefaultValue as PathValue<T, FieldPathByValue<T, boolean | undefined>>}
      shouldUnregister
      rules={{ validate: (value) => value !== undefined || t('form.oppvarming.common.installasjonsAr.error.missing') }}
      render={({ field: { value: knowsInstallationYear, onChange } }) => (
        <Fieldset>
          {label && <Fieldset.Legend>{label}</Fieldset.Legend>}
          <Radio
            label={t(`form.common.yes`)}
            name={vetInstallasjonsArFieldName}
            value="Ja"
            checked={knowsInstallationYear === true}
            onChange={() => onChange(true)}
            aria-invalid={!!radioError}
          />
          <Radio
            label={t(`form.common.no`)}
            name={vetInstallasjonsArFieldName}
            value="Nei"
            checked={knowsInstallationYear === false}
            onChange={() => onChange(false)}
            aria-invalid={!!radioError}
            description={
              knowsInstallationYear === false &&
              t(`${translationKeyPrefix}.input.disclaimer`, { byggAr: byggAr })
            }
          />

          {knowsInstallationYear === true && (
            <Textfield
              inputMode="numeric"
              maxLength={4}
              label={t('form.oppvarming.common.installasjonsAr.label')}
              description={installationIsKnownDescription}
              {...register(installasjonsArFieldName, {
                validate: (year) => validateYearAndBuildYear(year as number | undefined, byggAr),
                pattern: yearPattern,
              })}
            />
          )}
          {errorMessage && <ValidationMessage data-color="danger">{errorMessage}</ValidationMessage>}
        </Fieldset>
      )}
    />
  );
}